import cn from 'classnames'

/* The initiatives plan quality is already a cockpit reading: the Initiatives tile's `Plan Quality`
   tab computes it per offering in `selectCockpitInitiativesPlanQualityByOffering`, and draws it
   with `CockpitInitiativesQuality` — the three pillars, the tables of what each of them is short
   of, and the scoring dialogs behind them. This section shows that same reading rather than a
   second calculation of its own.

   It reads the per-offering entry and nothing else. The status is raised per offering, so that is
   the reading behind it: the tile's own headline number is scoped to whatever the cockpit is
   filtered to — the aggregated `Overall` plan on the overall view, and a single category once a
   category filter is on — and neither of those is what this status was raised about. */
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { INITIATIVES_SECTION_URL_VALUE, TAB_URL } from 'partner360/configs'
import { ATTENTION_STATUS_BITS } from 'partner360/entities/attentionStatuses'
import { p360TabsIds } from 'partner360/entities/partnerCompleteness'
import { TRANSLATION_KEYS as KEY_INITIATIVES_KEYS } from 'partner360/features/keyInitiatives/configs/translations'
import { PlanReviewSection } from 'partner360/features/planReview'
import { buildPlanReviewSectionUrl } from 'partner360/features/planReview/lib/buildPlanReviewSectionUrl'
import { scrollToElement } from 'partner360/helpers/scrollToElement'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import {
  selectCockpitInitiativesPlanQualityByOffering,
  selectCockpitPartnerData,
  selectEffectiveCockpitFYear,
} from '../../../../model/selectors'
import { CockpitSectionHeader } from '../../../components/cockpitSectionHeader'
import { useCockpitSectionNavigation } from '../../../components/cockpitSectionNavigation'
import { useAttentionDetailExit } from '../../hooks/useAttentionDetailExit'
import { useAttentionDetailOffering } from '../../hooks/useAttentionDetailOffering'
import { AttentionStatusActions } from '../attentionStatusActions'

import styles from './AttentionStatusInitiativesQuality.module.scss'

const DETAIL_KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.detail
const INITIATIVES_QUALITY_KEYS =
  FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.initiativesQuality
/**
 * Where `getQualityBand` draws its top band: a plan above this scores `High`, and the `Moderate`
 * or `Low` below it is what the status is raised about. The plate states the line the score is
 * read against; the band beside it comes from the calculation rather than from this number, so
 * the two cannot disagree.
 */
const HIGH_QUALITY_THRESHOLD = 80

/** The pillars the score is made of, named by the same keys the Initiatives tile titles them with
 *  — one word for one thing across the cockpit. */
const PILLARS = [
  {
    id: 'strategicGrowthAreas',
    titleKey: KEY_INITIATIVES_KEYS.scoreCards.strategicGrowthAreas.title,
  },
  { id: 'descriptionQuality', titleKey: KEY_INITIATIVES_KEYS.scoreCards.descriptionQuality.title },
  {
    id: 'financialPlanCoverage',
    titleKey: KEY_INITIATIVES_KEYS.scoreCards.financialPlanCoverage.title,
  },
] as const

/** The partner page's Key initiatives tab — where the plan behind the score is edited. The same
 *  tab `getCompletenessItems` sends its own `Initiatives plan` row to. */
const KEY_INITIATIVES_URL_PARAMS = { [TAB_URL]: INITIATIVES_SECTION_URL_VALUE }

/**
 * The detail of the `Initiatives Plan is not good` status: the offering's initiatives plan quality
 * against the line it has to clear, and the three pillars the score is made of — what the plan
 * covers financially, how well its initiatives are described, and which growth areas it reaches.
 *
 * The score is not re-evaluated here. The server decides that the status is raised; the section
 * only states the reading behind it, and the pillars name which of the three is holding it down.
 *
 * `Quality review` opens the plan review on the partner page, scoped to the partner, year and
 * offering this card is stated for — that is where a weak pillar is either fixed or justified.
 * `Go to section` opens the Key initiatives tab itself, where the initiatives are edited.
 */
export const AttentionStatusInitiativesQuality = () => {
  const { openSection, openUrl } = useCockpitSectionNavigation()
  const activeOffering = useAttentionDetailOffering()
  const { byOffering } = useSelector(selectCockpitInitiativesPlanQualityByOffering)
  const partnerData = useSelector(selectCockpitPartnerData)
  const fYear = useSelector(selectEffectiveCockpitFYear)

  /* The quality is computed for every offering the cockpit presents — so an offering without an
     entry means the payload carried no plan to measure for it, which the card says rather than
     filling in with zeroes. */
  const quality = useMemo(
    () => (activeOffering ? (byOffering.get(activeOffering) ?? null) : null),
    [byOffering, activeOffering]
  )

  const partnerId = partnerData[0]?.PartnerId ?? ''

  /* The cockpit sits on the partner's own page, so the section it points at is a tab of that page
     rather than another address: the tab, then the scroll to the strip that carries it. */
  const exitDetail = useAttentionDetailExit()

  const handleGoToSection = useCallback(() => {
    scrollToElement(p360TabsIds)
    openSection(KEY_INITIATIVES_URL_PARAMS)
    exitDetail()
  }, [exitDetail, openSection])

  /* The review is opened for the offering the status was raised for, which on `Overall` is not the
     one the page is showing — so the address is built from the card's own scope rather than from the
     page's. Any of the three initiatives sections builds the same address; the first one is
     passed because the builder asks for one. */
  const handleOpenQualityReview = useCallback(() => {
    if (!partnerId || !activeOffering) {
      return
    }

    openUrl(
      buildPlanReviewSectionUrl({
        section: PlanReviewSection.FinancialPlanCoverage,
        partnerId,
        fYear,
        programType: activeOffering,
      })
    )
    exitDetail()
  }, [exitDetail, openUrl, partnerId, activeOffering, fYear])

  /* The plan is measured per offering, and the cockpit's filters decide which offerings a partner
     shows: with none of them left there is nothing to score, and an empty card would read as a
     section that failed to load. */
  if (!activeOffering) {
    return (
      <div className={styles.root}>
        <p className={styles.empty}>
          <Translation span text={DETAIL_KEYS.noOffering} />
        </p>
      </div>
    )
  }

  return (
    <div className={styles.root}>
      {quality === null ? (
        <p className={styles.empty}>
          <Translation span text={INITIATIVES_QUALITY_KEYS.notMeasured} />
        </p>
      ) : (
        <>
          {/* The header carries the reading twice over: the score, and the line it is read
          against, so the plate below states the same fact in the size the eye reaches first. */}
          <CockpitSectionHeader
            actions={
              <span className={styles.headerScore}>
                {quality.overall.score === null ? '' : `${quality.overall.score} · `}
                <Translation
                  span
                  text={INITIATIVES_QUALITY_KEYS.threshold}
                  replaceParams={[{ key: '{threshold}', value: String(HIGH_QUALITY_THRESHOLD) }]}
                />
              </span>
            }
          >
            <Translation span text={INITIATIVES_QUALITY_KEYS.total} />
          </CockpitSectionHeader>

          <div className={styles.plate}>
            <span className={styles.plateValue}>
              {quality.overall.score === null ? (
                <Translation span text={INITIATIVES_QUALITY_KEYS.notScored} />
              ) : (
                quality.overall.score
              )}
            </span>
            <span className={styles.plateLabel}>
              <Translation
                span
                text={INITIATIVES_QUALITY_KEYS.required}
                replaceParams={[{ key: '{threshold}', value: String(HIGH_QUALITY_THRESHOLD) }]}
              />
            </span>
            <span className={styles.meter}>
              <span
                className={styles.meterFill}
                style={{ transform: `scaleX(${(quality.overall.score ?? 0) / 100})` }}
              />
            </span>
          </div>

          {/* One row per pillar: what it is, and the score it contributes. The rows scroll rather
          than growing past the tile's floor, so the bar under them keeps its place. */}
          <div className={styles.pillars}>
            {PILLARS.map(({ id, titleKey }) => (
              <div key={id} className={styles.pillar}>
                <span className={styles.pillarTitle}>
                  <Translation span text={titleKey} />
                </span>
                {/* A pillar the calculation skipped says so in words: a dash beside two zeroes
                reads as a third number the reader has to decode, and the three states — scored,
                scored at zero, not scored at all — are not a matter of formatting. */}
                <span
                  className={cn(styles.pillarScore, {
                    [styles.pillarScoreMissing]: quality[id].score === null,
                  })}
                >
                  {quality[id].score === null ? (
                    <Translation span text={INITIATIVES_QUALITY_KEYS.notScored} />
                  ) : (
                    `${quality[id].score}%`
                  )}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* The review is opened for a partner, and the cockpit carries none until its data lands —
      the button is then drawn disabled rather than drawn live and landing nowhere. */}
      <AttentionStatusActions
        bitValue={ATTENTION_STATUS_BITS.initiativesQuality}
        className={styles.actions}
        disabled={partnerId ? undefined : ['openQualityReview']}
        handlers={{
          openQualityReview: handleOpenQualityReview,
          goToSection: handleGoToSection,
        }}
      />
    </div>
  )
}

AttentionStatusInitiativesQuality.displayName = 'AttentionStatusInitiativesQuality'
